import React from "react";
import { motion } from "framer-motion";
import { Send, Instagram, Facebook, BadgeCheck } from "lucide-react";
import { trackSubscribe } from "@/lib/track";

const TikTokIcon = ({ className }) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
);

const socials = [
    {
        name: "Telegram",
        href: "https://t.me/mztradinglab",
        Icon: Send,
        color: "#229ED9",
        testid: "social-telegram",
    },
    {
        name: "Instagram",
        href: "https://www.instagram.com/muhamedzaidi_?igsh=MXdjbDIwam44ZjRoNA==",
        Icon: Instagram,
        color: "#E1306C",
        testid: "social-instagram",
    },
    {
        name: "Facebook",
        href: "https://www.facebook.com/profile.php?id=61592553352460",
        Icon: Facebook,
        color: "#1877F2",
        testid: "social-facebook",
    },
    {
        name: "TikTok",
        href: "https://www.tiktok.com/@muhamedzaidi?_r=1&_t=ZS-98H1FKOYtfR",
        Icon: TikTokIcon,
        color: "#25F4EE",
        testid: "social-tiktok",
    },
];

const SocialSection = () => {
    return (
        <section
            id="socials"
            data-testid="social-section"
            className="relative bg-[#0a0a0a] py-16 md:py-20 overflow-hidden"
        >
            <div className="absolute inset-0 hero-grid opacity-30 pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-40 w-[70%] bg-[#0077b6]/10 blur-[100px] pointer-events-none" />

            <div className="relative px-6 md:px-12 lg:px-24">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 max-w-4xl mx-auto">
                    {socials.map((s, i) => (
                        <motion.a
                            key={s.name}
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={s.testid}
                            onClick={trackSubscribe}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            className="group flex flex-col items-center gap-3"
                        >
                            <div className="relative">
                                <div
                                    className="absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-80 transition-opacity duration-300"
                                    style={{ backgroundColor: s.color }}
                                />
                                <div className="relative flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm group-hover:border-white/25 group-hover:-translate-y-1 transition-all duration-300">
                                    <s.Icon
                                        className="h-7 w-7 sm:h-8 sm:w-8"
                                        style={{ color: s.color }}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="font-display text-xs sm:text-sm font-semibold tracking-widest uppercase text-white/70 group-hover:text-white transition-colors">
                                    {s.name}
                                </span>
                                <BadgeCheck
                                    className="h-3.5 w-3.5"
                                    style={{ color: s.color }}
                                />
                            </div>
                        </motion.a>
                    ))}
                </div>

                <div className="mt-12 flex items-center justify-center gap-4 max-w-lg mx-auto">
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/15" />
                    <span className="font-display text-xs sm:text-sm font-medium tracking-[0.3em] uppercase text-white/40">
                        Connect With Us
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/15" />
                </div>
            </div>
        </section>
    );
};

export default SocialSection;
